import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Slider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Clear as ClearIcon,
  FilterAlt as FilterIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

interface NumberRangeFilterProps {
  min?: number | null;
  max?: number | null;
  onChange: (min: number | null, max: number | null) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  clearable?: boolean;
  // Data-driven range configuration
  dataMin?: number;
  dataMax?: number;
  targetField?: string; // Field name to calculate range from
  sourceData?: any[]; // Source data to calculate range from
}

const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: 0,
  backgroundColor: 'transparent',
  color: '#18181b',
  borderRadius: 0,
  border: 'none',
  boxShadow: 'none',
  '& .MuiTextField-root': {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      color: '#18181b',
      borderRadius: '6px',
      border: '1px solid #e4e4e7',
      fontFamily: "'Inter', sans-serif",
      fontSize: '14px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e4e4e7',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#18181b',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#18181b',
        borderWidth: '1px',
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: "'Inter', sans-serif",
      color: '#71717a',
      '&.Mui-focused': {
        color: '#18181b',
      },
    },
  },
}));

const RangeInputsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
  },
}));

const RangeSeparator = styled(Typography)(({ theme }) => ({
  color: '#71717a',
  fontFamily: "'Inter', sans-serif",
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  minWidth: 'auto',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

export const NumberRangeFilter: React.FC<NumberRangeFilterProps> = ({
  min,
  max,
  onChange,
  disabled = false,
  label = 'Number Range',
  placeholder = 'Enter range',
  clearable = true,
  dataMin,
  dataMax,
  targetField,
  sourceData
}) => {
  const [internalMin, setInternalMin] = useState<number | null>(min || null);
  const [internalMax, setInternalMax] = useState<number | null>(max || null);
  const [useSlider, setUseSlider] = useState(true); // Default to slider mode
  const [calculatedRange, setCalculatedRange] = useState<{min: number, max: number} | null>(null);
  const hasValidRange = React.useRef(false);

  // Calculate data range from source data
  useEffect(() => {
    if (sourceData && targetField) {
      const values = sourceData
        .map(row => {
          // Handle the overtime hours case (ot1_hours + ot2_hours)
          if (targetField === 'overtime_hours_total') {
            const ot1 = Number(row.ot1_hours) || 0;
            const ot2 = Number(row.ot2_hours) || 0;
            return ot1 + ot2;
          }
          return Number(row[targetField]) || 0;
        })
        .filter(val => !isNaN(val));
      
      if (values.length > 0) {
        const dataMin = Math.min(...values);
        const dataMax = Math.max(...values);
        
        // Only update the range if it's valid (max > min) or if we don't have a valid range yet
        // This prevents the slider from breaking when filtered data temporarily has no range
        if (dataMax > dataMin || !hasValidRange.current) {
          setCalculatedRange({ min: dataMin, max: dataMax });
          if (dataMax > dataMin) {
            hasValidRange.current = true;
          }
        } else {
        }
      }
    } else if (dataMin !== undefined && dataMax !== undefined) {
      // Only set if it's a valid range or we don't have a valid range yet
      if (dataMax > dataMin || !hasValidRange.current) {
        setCalculatedRange({ min: dataMin, max: dataMax });
        if (dataMax > dataMin) {
          hasValidRange.current = true;
        }
      }
    }
  }, [sourceData, targetField, dataMin, dataMax]);

  // Sync with external changes
  useEffect(() => {
    setInternalMin(min || null);
    setInternalMax(max || null);
  }, [min, max]);

  const handleMinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? null : Number(event.target.value);
    setInternalMin(value);
    onChange(value, internalMax);
  };

  const handleMaxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? null : Number(event.target.value);
    setInternalMax(value);
    onChange(internalMin, value);
  };

  const handleClear = () => {
    setInternalMin(null);
    setInternalMax(null);
    onChange(null, null);
  };

  // Slider handlers
  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      const [minVal, maxVal] = newValue;
      
      
      // Ensure the values are valid numbers and within the valid range
      if (isNaN(minVal) || isNaN(maxVal) || !calculatedRange) {
        console.warn(`🎚️ [NumberRangeFilter] Invalid slider values, ignoring:`, { minVal, maxVal, calculatedRange });
        return;
      }
      
      // Clamp values to the calculated range and ensure min <= max
      const clampedMin = Math.max(Math.min(minVal, maxVal), calculatedRange.min);
      const clampedMax = Math.min(Math.max(minVal, maxVal), calculatedRange.max);
      
      setInternalMin(clampedMin);
      setInternalMax(clampedMax);
      onChange(clampedMin, clampedMax);
      
    }
  };

  // Get effective range for slider
  const getEffectiveRange = () => {
    if (calculatedRange) return calculatedRange;
    return { min: 0, max: 100 }; // fallback range
  };

  const formatRangeDisplay = () => {
    if (internalMin === null && internalMax === null) return placeholder;
    if (internalMin !== null && internalMax !== null) {
      return `${internalMin} - ${internalMax}`;
    }
    if (internalMin !== null) return `≥ ${internalMin}`;
    if (internalMax !== null) return `≤ ${internalMax}`;
    return placeholder;
  };

  const hasValue = internalMin !== null || internalMax !== null;

  return (
    <FilterContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FilterIcon sx={{ color: '#71717a', fontSize: 20 }} />
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#18181b',
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '24px'
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {calculatedRange && (
            <Tooltip title="Switch input mode">
              <IconButton
                size="small"
                onClick={() => setUseSlider(!useSlider)}
                disabled={disabled}
                sx={{
                  color: '#71717a',
                  '&:hover': {
                    color: '#18181b',
                    backgroundColor: '#f4f4f5',
                  },
                }}
              >
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {clearable && hasValue && (
            <Tooltip title="Clear filter">
              <IconButton
                size="small"
                onClick={handleClear}
                disabled={disabled}
                sx={{
                  color: '#71717a',
                  '&:hover': {
                    color: '#18181b',
                    backgroundColor: '#f4f4f5',
                  },
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Typography 
        variant="body2" 
        sx={{ 
          color: '#71717a',
          fontFamily: "'Inter', sans-serif",
          fontSize: '14px',
          lineHeight: '20px',
          mb: 2 
        }}
      >
        {formatRangeDisplay()}
        {calculatedRange && (
          <span style={{ marginLeft: 8, fontSize: '0.85em', opacity: 0.7 }}>
            (Data range: {calculatedRange.min} - {calculatedRange.max})
          </span>
        )}
      </Typography>

      {calculatedRange && useSlider ? (
        // Slider Mode
        <Box sx={{ px: 1, pb: 2 }}>
          {calculatedRange.max > calculatedRange.min ? (
            <Slider
              value={(() => {
                // Calculate safe slider values
                const safeMin = internalMin !== null 
                  ? Math.max(Math.min(internalMin, calculatedRange.max), calculatedRange.min)
                  : calculatedRange.min;
                const safeMax = internalMax !== null 
                  ? Math.min(Math.max(internalMax, calculatedRange.min), calculatedRange.max)
                  : calculatedRange.max;
                
                // Ensure min <= max
                const finalMin = Math.min(safeMin, safeMax);
                const finalMax = Math.max(safeMin, safeMax);
                
                
                return [finalMin, finalMax];
              })()}
              onChange={handleSliderChange}
              valueLabelDisplay="auto"
              min={calculatedRange.min}
              max={calculatedRange.max}
              step={1}
              disabled={disabled}
              sx={{
                color: '#18181b',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#18181b',
                  '&:hover': {
                    boxShadow: '0px 0px 0px 8px rgba(24, 24, 27, 0.16)',
                  },
                },
                '& .MuiSlider-track': {
                  backgroundColor: '#18181b',
                },
                '& .MuiSlider-rail': {
                  backgroundColor: '#e4e4e7',
                },
                '& .MuiSlider-valueLabel': {
                  backgroundColor: '#18181b',
                  color: '#ffffff',
                  fontFamily: "'Inter', sans-serif",
                },
              }}
            />
          ) : (
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#71717a',
                fontFamily: "'Inter', sans-serif",
                fontSize: '14px',
                lineHeight: '20px',
                textAlign: 'center', 
                py: 2 
              }}
            >
              No range available (all values are {calculatedRange.min})
            </Typography>
          )}
        </Box>
      ) : (
        // Manual Input Mode
        <RangeInputsContainer>
          <TextField
            label="Min"
            type="number"
            value={internalMin !== null ? internalMin : ''}
            onChange={handleMinChange}
            size="small"
            disabled={disabled}
            sx={{ 
              minWidth: 100,
              flex: { xs: 1, sm: 0 },
              fontFamily: "'Inter', sans-serif",
            }}
            inputProps={{
              step: 'any',
            }}
          />
          
          <RangeSeparator>to</RangeSeparator>
          
          <TextField
            label="Max"
            type="number"
            value={internalMax !== null ? internalMax : ''}
            onChange={handleMaxChange}
            size="small"
            disabled={disabled}
            sx={{ 
              minWidth: 100,
              flex: { xs: 1, sm: 0 },
              fontFamily: "'Inter', sans-serif",
            }}
            inputProps={{
              step: 'any',
            }}
          />
        </RangeInputsContainer>
      )}
    </FilterContainer>
  );
}; 